import React, { useEffect, useState } from 'react';
import {
  Box, Button, Divider, ListItemIcon, ListItemText, Menu, MenuItem, Popover, Stack, TextField, Typography,
} from '@mui/material';
import {
  ArrowDropDown as CaretIcon,
  CalendarMonthOutlined as DateIcon,
  Check as CheckIcon,
} from '@mui/icons-material';

// The one filter bar every chart screen uses: a row under the title, the
// way analytics tools lay it out. A date range first (presets, or a custom
// range applied with a button), then one drop-down per dimension, then
// "Clear filters". Each button names its current value and turns blue when
// it differs from the screen's default, so the row reads as a sentence of
// what is on screen. Nothing appears or disappears: a screen's row is the
// same controls every time.
//
// Payroll & revenue was the first (pages/payroll/ReportFilters); these are
// its parts, so Timeline, Weekly volume and Hiring look and behave the same.

export interface DateRange { from: string; to: string }
export interface RangePreset { key: string; label: string; range: DateRange }

const fmt = (d: string, year = false) => new Date(`${d}T00:00:00Z`)
  .toLocaleDateString('en-US', { month: 'short', day: 'numeric', ...(year && { year: 'numeric' }), timeZone: 'UTC' });

/** "Last 8 weeks", or "Mar 1 – Apr 30, 2026" for a range no preset names. */
export const rangeLabel = (range: DateRange | null, list: RangePreset[], empty = 'No weeks yet') => {
  if (!range) return empty;
  const named = list.find((p) => p.range.from === range.from && p.range.to === range.to);
  return named ? named.label : `${fmt(range.from)} – ${fmt(range.to, true)}`;
};

// One button in the bar: an icon, the dimension, its value, a caret.
export const FilterButton: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: string;
  active: boolean;
  disabled?: boolean;
  onClick: (e: React.MouseEvent<HTMLElement>) => void;
}> = ({ icon, label, value, active, disabled, onClick }) => (
  <Button
    size="small"
    variant="outlined"
    color={active ? 'primary' : 'inherit'}
    startIcon={icon}
    endIcon={<CaretIcon />}
    disabled={disabled}
    onClick={onClick}
    aria-haspopup="true"
    aria-label={`${label}: ${value}`}
    sx={{
      height: 32,
      px: 1.25,
      fontWeight: 500,
      whiteSpace: 'nowrap',
      borderColor: active ? 'primary.main' : 'divider',
      bgcolor: active ? 'primary.light' : 'background.paper',
      color: active ? 'primary.dark' : 'text.primary',
      '& .MuiButton-startIcon': { mr: 0.75, color: active ? 'primary.main' : 'text.secondary' },
      '& .MuiButton-endIcon': { ml: 0.25, color: 'text.secondary' },
    }}
  >
    <Box component="span" sx={{ color: active ? 'primary.dark' : 'text.secondary', mr: 0.5 }}>{label}</Box>
    <Box component="span" sx={{ fontWeight: 600, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis' }}>{value}</Box>
  </Button>
);

export interface Choice<K extends string | number> {
  key: K;
  label: string;
  /** Short text on the button, when the menu's wording is longer. */
  short?: string;
  /** A count or note at the right of the menu row. */
  note?: React.ReactNode;
}

/** A dimension with one value at a time: a button and a checked menu. */
export function ChoiceFilter<K extends string | number>({
  icon, label, value, choices, onChange, defaultValue, disabled,
}: {
  icon: React.ReactNode;
  label: string;
  value: K;
  choices: Choice<K>[];
  onChange: (k: K) => void;
  /** Blue when the value is not this one. */
  defaultValue: K;
  disabled?: boolean;
}) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const current = choices.find((c) => c.key === value);
  return (
    <>
      <FilterButton
        icon={icon}
        label={label}
        value={current ? (current.short || current.label) : '—'}
        active={value !== defaultValue}
        disabled={disabled || !choices.length}
        onClick={(e) => setAnchor(e.currentTarget)}
      />
      <Menu open={!!anchor} anchorEl={anchor} onClose={() => setAnchor(null)} slotProps={{ paper: { sx: { mt: 0.5, minWidth: 200 } } }}>
        {choices.map((c) => (
          <MenuItem key={String(c.key)} dense selected={c.key === value} onClick={() => { onChange(c.key); setAnchor(null); }}>
            <ListItemIcon>{c.key === value && <CheckIcon fontSize="small" />}</ListItemIcon>
            <ListItemText primary={c.label} />
            {c.note !== undefined && <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>{c.note}</Typography>}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

/**
 * The date range: presets apply at once; a custom range waits for Apply.
 * `describe` says, under the custom fields, what the typed range holds.
 */
export const DateRangeFilter: React.FC<{
  label?: string;
  range: DateRange | null;
  presets: RangePreset[];
  onChange: (r: DateRange) => void;
  /** Blue when the range is not this preset. */
  defaultKey: string;
  disabled?: boolean;
  empty?: string;
  describe?: (r: DateRange) => string;
  hint?: string;
}> = ({
  label = 'Date', range, presets, onChange, defaultKey, disabled, empty, describe, hint = 'A week counts when any of its days is in the range.',
}) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  useEffect(() => { if (anchor) { setFrom(range?.from || ''); setTo(range?.to || ''); } }, [anchor, range]);
  const backwards = !!from && !!to && from > to;
  const current = presets.find((p) => range && p.range.from === range.from && p.range.to === range.to);
  return (
    <>
      <FilterButton
        icon={<DateIcon />}
        label={label}
        value={rangeLabel(range, presets, empty)}
        active={!!range && current?.key !== defaultKey}
        disabled={disabled}
        onClick={(e) => setAnchor(e.currentTarget)}
      />
      <Popover open={!!anchor} anchorEl={anchor} onClose={() => setAnchor(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }} slotProps={{ paper: { sx: { mt: 0.5 } } }}>
        <Stack direction={{ xs: 'column', sm: 'row' }}>
          <Box component="ul" sx={{ m: 0, p: 0.75, minWidth: 180, listStyle: 'none' }} role="listbox" aria-label="Date presets">
            {presets.map((p) => (
              <MenuItem key={p.key} dense selected={current?.key === p.key} onClick={() => { onChange(p.range); setAnchor(null); }} sx={{ borderRadius: 1 }}>
                {p.label}
              </MenuItem>
            ))}
          </Box>
          <Divider orientation="vertical" flexItem />
          <Stack component="form" gap={1.5} sx={{ p: 2, width: 260 }} onSubmit={(e: React.FormEvent) => { e.preventDefault(); if (from && to && !backwards) { onChange({ from, to }); setAnchor(null); } }}>
            <Typography variant="subtitle2">Custom range</Typography>
            <TextField size="small" type="date" label="From" value={from} onChange={(e) => setFrom(e.target.value)} InputLabelProps={{ shrink: true }} error={backwards} />
            <TextField size="small" type="date" label="To" value={to} onChange={(e) => setTo(e.target.value)} InputLabelProps={{ shrink: true }} error={backwards} helperText={backwards ? 'From is after To' : undefined} />
            <Typography variant="caption" color="text.secondary">
              {from && to && !backwards && describe ? describe({ from, to }) : hint}
            </Typography>
            <Stack direction="row" justifyContent="flex-end" gap={1}>
              <Button size="small" onClick={() => setAnchor(null)}>Cancel</Button>
              <Button size="small" type="submit" variant="contained" disableElevation disabled={!from || !to || backwards}>Apply</Button>
            </Stack>
          </Stack>
        </Stack>
      </Popover>
    </>
  );
};

/** The row itself, ending in "Clear filters". */
export const FilterBar: React.FC<{ children: React.ReactNode; onReset?: () => void; canReset?: boolean }> = ({ children, onReset, canReset }) => (
  <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center">
    {children}
    {onReset && <Button size="small" disabled={!canReset} onClick={onReset} sx={{ height: 32 }}>Clear filters</Button>}
  </Stack>
);
